import { useState, useEffect, useRef, type FormEvent } from 'react';
import axios from 'axios';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const Icons = {
  Check: () => <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
  AlertCircle: () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
  Loader: () => <svg className="animate-spin" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/></svg>,
  Info: () => <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>,
  X: () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  Plus: () => <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
};

function TimeInput({ value, onChange, disabled, className }: {
  value: string
  onChange: (v: string) => void
  disabled?: boolean
  className?: string
}) {
  const [display, setDisplay] = useState(value)
  const lastValidRef = useRef(value)

  useEffect(() => {
    setDisplay(value)
    lastValidRef.current = value
  }, [value])

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const digits = e.target.value.replace(/[^0-9]/g, '').slice(0, 4)
    if (digits.length === 0) { setDisplay(''); return }

    let formatted: string
    if (digits.length <= 2) {
      formatted = digits
    } else {
      formatted = `${digits.slice(0, 2)}:${digits.slice(2)}`
    }
    setDisplay(formatted)

    if (formatted.length === 5) {
      const h = parseInt(formatted.slice(0, 2), 10)
      const m = parseInt(formatted.slice(3), 10)
      if (h <= 23 && m <= 59) {
        lastValidRef.current = formatted
        onChange(formatted)
      }
    }
  }

  function handleBlur() {
    if (display.length === 5) {
      const h = parseInt(display.slice(0, 2), 10)
      const m = parseInt(display.slice(3), 10)
      if (h <= 23 && m <= 59) { return }
    }
    setDisplay(lastValidRef.current)
  }

  return (
    <input
      type="text"
      value={display}
      onChange={handleChange}
      onBlur={handleBlur}
      disabled={disabled}
      placeholder="HH:MM"
      maxLength={5}
      inputMode="numeric"
      className={className}
    />
  )
}

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

interface AdminSettingsProps {
  getToken: () => Promise<string>;
  onResourceChange?: () => void;
}

type Tab = 'general' | 'hours' | 'appointments' | 'resources' | 'email' | 'datenschutz' | 'billing';

interface AdminProfile {
  business_name: string;
  contact_email: string;
}

interface BreakPeriod { start: string; end: string; }
interface DayHours { start: string; end: string; enabled: boolean; breaks?: BreakPeriod[]; }
interface Settings {
  business_hours?: { monday?: DayHours; tuesday?: DayHours; wednesday?: DayHours; thursday?: DayHours; friday?: DayHours; saturday?: DayHours; sunday?: DayHours; };
  default_duration?: number;
  default_gap?: number;
  timezone?: string;
  resources?: string[];
  appointment_durations?: { mode: 'single' | 'multiple'; default: number; types?: Array<{ id: string; name: string; duration: number; color?: string; available_for?: string[]; }>; };
  gap_rules?: { mode: 'strict' | 'flexible' | 'none'; min_gap_minutes?: number; gap_step?: number; allow_zero_gap?: boolean; prevent_unfillable_gaps?: boolean; };
  resource_label?: { singular: string; plural: string; };
}

// Shared input/select/label styles
const inputCls = "w-full bg-gray-50 border border-[#E8EAF6] text-gray-900 text-sm px-3.5 py-2.5 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF6D00]/15 focus:border-[#FF6D00] transition-all placeholder:text-gray-400 disabled:opacity-50 disabled:cursor-not-allowed";
const labelCls = "block text-xs font-bold text-[#654F6F] uppercase tracking-widest mb-2";
const saveBtnCls = "px-5 py-2.5 bg-gradient-to-r from-[#FF6D00] to-[#FF9A00] text-white text-sm font-bold rounded-xl shadow-[0_4px_12px_rgba(255,109,0,0.25)] hover:shadow-[0_6px_18px_rgba(255,109,0,0.35)] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2";

const TAB_CONFIG: { key: Tab; label: string; icon: React.ReactNode; }[] = [
  { key: 'general', label: 'Allgemein', icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/></svg> },
  { key: 'hours', label: 'Öffnungszeiten', icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg> },
  { key: 'appointments', label: 'Termine', icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg> },
  { key: 'resources', label: 'Ressourcen', icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg> },
  { key: 'email', label: 'E-Mail', icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg> },
  { key: 'datenschutz', label: 'Datenschutz', icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg> },
  { key: 'billing', label: 'Abonnement', icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg> },
];

// ============================================================================
// BILLING TAB
// ============================================================================

function BillingTab({ getToken }: { getToken: () => Promise<string> }) {
  const [status, setStatus] = useState<{
    subscription_status: string | null;
    subscription_current_period_end: number | null;
    subscription_cancel_at_period_end: boolean;
    api_active?: boolean;
    payment_grace_ends_at?: number | null;
    has_billing_portal?: boolean;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [starting, setStarting] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cancelSuccess, setCancelSuccess] = useState(false);

  useEffect(() => { loadStatus(); }, []);

  const loadStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await axios.get(`${API_BASE_URL}/admin/stripe/subscription-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(res.data);
    } catch {
      setError('Abo-Status konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartCheckout = async () => {
    setStarting(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await axios.post(`${API_BASE_URL}/admin/stripe/create-checkout-session`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      window.location.href = res.data.url;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Starten des Zahlungsvorgangs.');
      setStarting(false);
    }
  };

  const handleOpenPortal = async () => {
    setOpeningPortal(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await axios.post(`${API_BASE_URL}/admin/stripe/portal-session`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      window.location.href = res.data.url;
    } catch (err) {
      const message = axios.isAxiosError(err) ? err.response?.data?.error : null;
      setError(message || 'Kundenportal konnte nicht geöffnet werden.');
      setOpeningPortal(false);
    }
  };

  const handleCancel = async () => {
    if (!confirm('Möchtest du dein Abo wirklich kündigen? Du behältst den Zugang bis zum Ende der aktuellen Periode.')) return;
    setCancelling(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await axios.post(`${API_BASE_URL}/admin/stripe/cancel-subscription`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCancelSuccess(true);
      setStatus(prev => prev ? {
        ...prev,
        subscription_cancel_at_period_end: true,
        subscription_current_period_end: res.data.current_period_end,
      } : prev);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Kündigung fehlgeschlagen. Bitte versuche es erneut.');
    } finally {
      setCancelling(false);
    }
  };

  const formatDate = (unixTs: number) =>
    new Date(unixTs * 1000).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' });

  const statusLabel: Record<string, { label: string; color: string; bg: string }> = {
    trialing:  { label: 'Kostenlose Testphase', color: '#0076E8', bg: '#E8F4FF' },
    active:    { label: 'Aktiv',                color: '#059669', bg: '#ECFDF5' },
    past_due:  { label: 'Zahlung ausstehend',   color: '#D97706', bg: '#FFFBEB' },
    canceled:  { label: 'Gekündigt',            color: '#DC2626', bg: '#FEF2F2' },
    paused:    { label: 'Pausiert',             color: '#7C3AED', bg: '#F5F3FF' },
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="w-8 h-8 border-2 border-[#E8EAF6] border-t-[#FF6D00] rounded-full animate-spin" />
      </div>
    );
  }

  const s = status?.subscription_status ?? null;
  const info = s ? (statusLabel[s] ?? { label: s, color: '#654F6F', bg: '#F4F5FC' }) : null;
  const periodEnd = status?.subscription_current_period_end;
  const cancelAtEnd = status?.subscription_cancel_at_period_end;
  const hasActiveSubscription = s && s !== 'canceled';

  const periodLabel = cancelAtEnd ? 'Zugang bis' : s === 'trialing' ? 'Testphase endet am' : 'Nächste Abbuchung';
  const headingFont = { fontFamily: "'Plus Jakarta Sans', sans-serif" };
  const cardCls = "bg-white rounded-[18px] border border-[#E8EAF6] shadow-[0_2px_12px_rgba(14,11,30,0.04)] overflow-hidden";

  const statusHeadline = !s
    ? 'Kein aktives Abonnement'
    : s === 'trialing' ? 'Kostenlose Testphase'
    : s === 'active' ? 'Abonnement aktiv'
    : s === 'past_due' ? 'Zahlung ausstehend'
    : s === 'canceled' ? 'Abonnement gekündigt'
    : 'Abonnement pausiert';

  const statusText = !s
    ? 'Starte jetzt – der erste Monat ist kostenlos.'
    : s === 'canceled' ? 'Du kannst jederzeit ein neues Abonnement starten.'
    : s === 'past_due'
      ? status?.api_active === false
        ? 'Deine letzte Zahlung ist seit mehr als 14 Tagen offen. Chat-Bot und Widget sind deaktiviert, bis die Zahlung eingegangen ist.'
        : `Deine letzte Zahlung konnte nicht eingezogen werden. Bitte aktualisiere deine Zahlungsmethode${status?.payment_grace_ends_at ? `, sonst werden Chat-Bot und Widget am ${new Date(status.payment_grace_ends_at).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })} deaktiviert` : ''}.`
    : cancelAtEnd ? `Gekündigt – dein Zugang bleibt bis zum ${periodEnd ? formatDate(periodEnd) : 'Ende der Periode'} bestehen.`
    : 'Chat-Bot und Widget stehen dir uneingeschränkt zur Verfügung.';

  const startLabel = !s ? 'Abo jetzt starten (30 Tage kostenlos)' : 'Neues Abo starten';
  const showStart = !s || s === 'canceled';

  const rows: { label: string; value: React.ReactNode }[] = [
    { label: 'Tarif', value: 'Candlear Booking Pro' },
    { label: 'Abrechnung', value: 'Monatlich' },
    ...(info ? [{
      label: 'Status',
      value: (
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded-md" style={{ color: info.color, background: info.bg }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: info.color }} />
          {info.label}
        </span>
      ),
    }] : []),
    ...(periodEnd && s !== 'canceled' ? [{ label: periodLabel, value: formatDate(periodEnd) }] : []),
  ];

  return (
    <div className="space-y-5">

      {/* Notifications */}
      {error && (
        <div role="alert" className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
          <span className="mt-0.5 flex-shrink-0"><Icons.AlertCircle /></span>
          <span>{error}</span>
        </div>
      )}
      {cancelSuccess && (
        <div role="status" className="flex items-start gap-2.5 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-sm text-emerald-700">
          <span className="mt-0.5 flex-shrink-0"><Icons.Check /></span>
          <span>Kündigung bestätigt. Du behältst den Zugang bis zum Ende der aktuellen Periode.</span>
        </div>
      )}

      {/* Plan card */}
      <section className={cardCls}>
        <header className="px-4 sm:px-6 py-4 border-b border-[#F0F2FA] flex items-center justify-between gap-4">
          <div>
            <h3 className="text-sm font-bold text-[#0E0B1E]" style={headingFont}>Abonnement</h3>
            <p className="text-xs text-[#654F6F] mt-0.5">Dein Tarif und Abrechnungsstatus</p>
          </div>
          <button
            onClick={loadStatus}
            className="text-xs font-semibold text-[#654F6F] hover:text-[#0E0B1E] border border-[#E8EAF6] hover:border-[#D5D9EE] rounded-lg px-3 py-1.5 transition-colors"
            title="Status aktualisieren"
          >
            Aktualisieren
          </button>
        </header>

        <div className="px-4 sm:px-6 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="min-w-0">
            <p className="text-base font-bold text-[#0E0B1E]" style={headingFont}>{statusHeadline}</p>
            <p className={cn('text-sm mt-1 leading-relaxed', s === 'past_due' ? 'text-amber-700' : 'text-[#654F6F]')}>{statusText}</p>
          </div>
          {showStart && (
            <button
              onClick={handleStartCheckout}
              disabled={starting}
              className={cn(saveBtnCls, 'flex-shrink-0 justify-center')}
              style={headingFont}
            >
              {starting ? <><Icons.Loader /><span>Wird gestartet...</span></> : <span>{startLabel}</span>}
            </button>
          )}
        </div>

        <dl className="border-t border-[#F0F2FA] divide-y divide-[#F0F2FA]">
          {rows.map(r => (
            <div key={r.label} className="px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
              <dt className="text-xs font-semibold text-[#654F6F]">{r.label}</dt>
              <dd className="text-sm font-medium text-[#0E0B1E] text-right">{r.value}</dd>
            </div>
          ))}
        </dl>
      </section>

      {/* Rechnungen und Zahlungsmethode (Stripe-Kundenportal) */}
      {status?.has_billing_portal && (
        <section className={cardCls}>
          <div className="px-6 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="min-w-0">
              <h3 className="text-sm font-bold text-[#0E0B1E]" style={headingFont}>Rechnungen und Zahlungsmethode</h3>
              <p className="text-sm text-[#654F6F] mt-1 leading-relaxed">
                Rechnungen herunterladen und deine Zahlungsmethode ändern. Du wirst dafür zu Stripe weitergeleitet.
              </p>
            </div>
            <button
              onClick={handleOpenPortal}
              disabled={openingPortal}
              className="flex-shrink-0 flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-[#0E0B1E] border border-[#E8EAF6] bg-white hover:bg-[#F4F5FC] hover:border-[#D5D9EE] active:bg-[#E8EAF6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6D00]/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={headingFont}
            >
              {openingPortal ? <><Icons.Loader />Wird geöffnet...</> : 'Rechnungen und Zahlung'}
            </button>
          </div>
        </section>
      )}

      {/* Cancel card */}
      {hasActiveSubscription && !cancelAtEnd && (
        <section className={cardCls}>
          <div className="px-4 sm:px-6 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="min-w-0">
              <h3 className="text-sm font-bold text-[#0E0B1E]" style={headingFont}>Abonnement kündigen</h3>
              <p className="text-sm text-[#654F6F] mt-1 leading-relaxed">
                Dein Zugang bleibt bis zum{periodEnd ? ` ${formatDate(periodEnd)}` : ' Ende der aktuellen Periode'} erhalten. Es wird keine weitere Zahlung eingezogen.
              </p>
            </div>
            <button
              onClick={handleCancel}
              disabled={cancelling}
              className="flex-shrink-0 flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-red-600 border border-[#E8EAF6] bg-white hover:bg-red-50 hover:border-red-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={headingFont}
            >
              {cancelling ? <><Icons.Loader />Wird verarbeitet...</> : 'Kündigen'}
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

export default function AdminSettings({ getToken, onResourceChange }: AdminSettingsProps) {
  const [activeTab, setActiveTab] = useState<Tab>('general');
  const [settings, setSettings] = useState<Settings | null>(null);
  const [profile, setProfile] = useState<AdminProfile>({ business_name: '', contact_email: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async (options?: { silent?: boolean }) => {
    const silent = options?.silent === true;
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const res = await axios.get(`${API_BASE_URL}/admin/settings`, { headers: { Authorization: `Bearer ${await getToken()}` } });
      setSettings(res.data.settings || {});
      setProfile({
        business_name: res.data.business_name || '',
        contact_email: res.data.contact_email || '',
      });
      if (!silent) setError(null);
    } catch (err: any) {
      if (!silent) setError('Fehler beim Laden der Einstellungen');
      else console.error('[SETTINGS] Silent settings refresh failed:', err);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  /** PATCH nur resource_label — überschreibt nicht versehentlich Ressourcen mit veraltetem State. */
  const saveResourceLabels = async (singular: string, plural: string) => {
    setSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      await axios.patch(
        `${API_BASE_URL}/admin/settings`,
        { settings: { resource_label: { singular, plural } } },
        { headers: { Authorization: `Bearer ${await getToken()}` } }
      );
      setSettings((prev) =>
        prev ? { ...prev, resource_label: { singular, plural } } : prev
      );
      setSuccessMessage('Einstellungen erfolgreich gespeichert!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Speichern der Einstellungen');
    } finally {
      setSaving(false);
    }
  };

  const saveSettings = async (updatedSettings: Settings) => {
    setSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      await axios.put(`${API_BASE_URL}/admin/settings`, { settings: updatedSettings }, { headers: { Authorization: `Bearer ${await getToken()}` } });
      setSettings(updatedSettings);
      setSuccessMessage('Einstellungen erfolgreich gespeichert!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Speichern der Einstellungen');
    } finally { setSaving(false); }
  };

  const saveProfile = async (updatedProfile: AdminProfile) => {
    setSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      await axios.patch(`${API_BASE_URL}/admin/settings`, updatedProfile, { headers: { Authorization: `Bearer ${await getToken()}` } });
      setProfile(updatedProfile);
      setSuccessMessage('Einstellungen erfolgreich gespeichert!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Speichern der Einstellungen');
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-[#E8EAF6] border-t-[#FF6D00] rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-400 font-medium">Lade Einstellungen...</p>
        </div>
      </div>
    );
  }

  if (error && !settings) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-12 h-12 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-3 text-red-500"><Icons.AlertCircle /></div>
          <p className="text-sm text-[#0E0B1E] font-semibold mb-3">{error}</p>
          <button type="button" onClick={() => fetchSettings()} className={saveBtnCls}>Erneut versuchen</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Success Toast */}
      <AnimatePresence>
        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3 shadow-sm"
          >
            <div className="w-6 h-6 bg-emerald-500 rounded-lg flex items-center justify-center text-white flex-shrink-0"><Icons.Check /></div>
            <p className="text-sm font-semibold text-emerald-800">{successMessage}</p>
          </motion.div>
        )}
      </AnimatePresence>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
          <div className="text-red-500 flex-shrink-0"><Icons.AlertCircle /></div>
          <p className="text-sm font-semibold text-red-800">{error}</p>
        </div>
      )}

      {/* Settings Grid: sidebar nav + content */}
      <div className="grid grid-cols-1 md:grid-cols-[220px_minmax(0,1fr)] gap-4 md:gap-5">
        {/* Left sidebar nav — auf dem Handy als waagerecht scrollbare Leiste */}
        <div className="min-w-0">
          <p className="hidden md:block text-[10px] font-bold text-[#654F6F] uppercase tracking-widest mb-3 px-1">Einstellungen</p>
          <div className="bg-white rounded-[14px] md:rounded-[18px] border border-[#E8EAF6] shadow-[0_2px_12px_rgba(14,11,30,0.04)] p-1.5 md:p-2">
            <nav className="flex md:flex-col gap-0.5 overflow-x-auto md:overflow-visible [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {TAB_CONFIG.map(tab => (
                <button
                  key={tab.key}
                  onClick={(e) => { setActiveTab(tab.key); e.currentTarget.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' }); }}
                  className={cn(
                    "flex items-center gap-2 px-3 min-h-10 md:min-h-0 md:py-2 rounded-[10px] text-xs font-semibold text-left whitespace-nowrap flex-shrink-0 transition-all",
                    activeTab === tab.key
                      ? "bg-[rgba(255,109,0,0.08)] text-[#FF6D00] font-bold"
                      : "text-[#654F6F] hover:bg-[#F4F5FC] hover:text-[#0E0B1E]"
                  )}
                >
                  <span className="flex-shrink-0">{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Right content panels */}
        <div className="min-w-0">
          {activeTab === 'general' && <GeneralTab settings={settings} onSave={saveSettings} saving={saving} />}
          {activeTab === 'hours' && <BusinessHoursTab settings={settings} onSave={saveSettings} saving={saving} />}
          {activeTab === 'appointments' && <AppointmentsTab settings={settings} onSave={saveSettings} saving={saving} />}
          {activeTab === 'resources' && (
            <ResourcesTab
              settings={settings}
              saving={saving}
              onResourceChange={onResourceChange}
              getToken={getToken}
              onSaveResourceLabels={saveResourceLabels}
              onSettingsSynced={() => fetchSettings({ silent: true })}
            />
          )}
          {activeTab === 'email' && <EmailTab profile={profile} onSaveProfile={saveProfile} saving={saving} />}
          {activeTab === 'datenschutz' && <DatenschutzTab getToken={getToken} />}
          {activeTab === 'billing' && <BillingTab getToken={getToken} />}
        </div>
      </div>
    </div>
  );
}

// --- GENERAL TAB ---
function GeneralTab({ settings, onSave, saving }: { settings: Settings | null; onSave: (s: Settings) => void; saving: boolean }) {
  const [timezone, setTimezone] = useState(settings?.timezone || 'Europe/Berlin');

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSave({ ...settings, timezone });
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="bg-white rounded-[18px] border border-[#E8EAF6] shadow-[0_2px_12px_rgba(14,11,30,0.04)] overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-[#E8EAF6] flex items-center justify-between">
          <div>
            <div className="text-sm font-bold text-[#0E0B1E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Allgemeine Einstellungen</div>
            <div className="text-xs text-[#654F6F] mt-0.5">Grundlegende Konfiguration deines Buchungssystems</div>
          </div>
        </div>
        <div className="p-4 sm:p-6">
          <div>
            <label className={labelCls}>Zeitzone</label>
            <select value={timezone} onChange={e => setTimezone(e.target.value)} className={inputCls}>
              <option value="Europe/Berlin">Europa/Berlin (MEZ)</option>
              <option value="Europe/Vienna">Europa/Wien (MEZ)</option>
              <option value="Europe/Zurich">Europa/Zürich (MEZ)</option>
              <option value="Europe/London">Europa/London (GMT)</option>
              <option value="America/New_York">Amerika/New York (EST)</option>
            </select>
            <p className="text-xs text-gray-400 mt-1.5">Alle Termine werden in dieser Zeitzone angezeigt.</p>
          </div>
        </div>
      </div>
      <div className="flex justify-end">
        <button type="submit" disabled={saving} className={saveBtnCls} style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          {saving ? <><Icons.Loader /><span>Speichert...</span></> : <><Icons.Check /><span>Speichern</span></>}
        </button>
      </div>
    </form>
  );
}

// --- BUSINESS HOURS TAB ---
type DayKey = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

const DEFAULT_DAY_HOURS: Record<DayKey, DayHours> = {
  monday: { start: '08:00', end: '18:00', enabled: true, breaks: [] },
  tuesday: { start: '08:00', end: '18:00', enabled: true, breaks: [] },
  wednesday: { start: '08:00', end: '18:00', enabled: true, breaks: [] },
  thursday: { start: '08:00', end: '18:00', enabled: true, breaks: [] },
  friday: { start: '08:00', end: '18:00', enabled: true, breaks: [] },
  saturday: { start: '08:00', end: '18:00', enabled: false, breaks: [] },
  sunday: { start: '08:00', end: '18:00', enabled: false, breaks: [] },
};

function BusinessHoursTab({ settings, onSave, saving }: { settings: Settings | null; onSave: (s: Settings) => void; saving: boolean }) {
  const days: DayKey[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const dayLabels: Record<DayKey, string> = { monday: 'Montag', tuesday: 'Dienstag', wednesday: 'Mittwoch', thursday: 'Donnerstag', friday: 'Freitag', saturday: 'Samstag', sunday: 'Sonntag' };

  const [businessHours, setBusinessHours] = useState<Record<DayKey, DayHours>>(() => {
    const saved = settings?.business_hours;
    if (!saved) return DEFAULT_DAY_HOURS;
    return days.reduce((acc, day) => {
      acc[day] = { ...DEFAULT_DAY_HOURS[day], ...saved[day], breaks: saved[day]?.breaks ?? [] };
      return acc;
    }, {} as Record<DayKey, DayHours>);
  });

  const [pendingBreak, setPendingBreak] = useState<Record<DayKey, { start: string; end: string; visible: boolean; applyToAll: boolean }>>(
    () => days.reduce((acc, day) => { acc[day] = { start: '12:00', end: '13:00', visible: false, applyToAll: false }; return acc; }, {} as Record<DayKey, { start: string; end: string; visible: boolean; applyToAll: boolean }>)
  );

  const handleDayChange = (day: DayKey, field: 'start' | 'end' | 'enabled', value: string | boolean) => {
    setBusinessHours(prev => ({ ...prev, [day]: { ...prev[day], [field]: value } }));
  };

  const handleDeleteBreak = (day: DayKey, idx: number) => {
    setBusinessHours(prev => ({ ...prev, [day]: { ...prev[day], breaks: (prev[day].breaks ?? []).filter((_, i) => i !== idx) } }));
  };

  const handleShowAddBreak = (day: DayKey) => {
    const dayStart = businessHours[day]?.start || '08:00';
    const dayEnd = businessHours[day]?.end || '18:00';
    const [sh, sm] = dayStart.split(':').map(Number);
    const [eh, em] = dayEnd.split(':').map(Number);
    const midpoint = Math.floor((sh * 60 + sm + eh * 60 + em) / 2);
    const brkStart = `${String(Math.floor(midpoint / 60)).padStart(2, '0')}:${String(midpoint % 60).padStart(2, '0')}`;
    const brkEndMin = midpoint + 60;
    const brkEnd = `${String(Math.floor(brkEndMin / 60)).padStart(2, '0')}:${String(brkEndMin % 60).padStart(2, '0')}`;
    setPendingBreak(prev => ({ ...prev, [day]: { start: brkStart, end: brkEnd, visible: true, applyToAll: false } }));
  };

  const handleConfirmBreak = (day: DayKey) => {
    const pb = pendingBreak[day];
    if (pb.start >= pb.end) { alert('Pausenanfang muss vor Pausenende liegen.'); return; }
    if (pb.applyToAll) {
      const skipped: string[] = [];
      setBusinessHours(prev => {
        const next = { ...prev };
        for (const d of days) {
          if (!prev[d].enabled) continue;
          const dStart = prev[d].start || '00:00';
          const dEnd = prev[d].end || '23:59';
          if (pb.start < dStart || pb.end > dEnd) { skipped.push(dayLabels[d]); continue; }
          const existing = prev[d].breaks ?? [];
          const overlaps = existing.some(b => pb.start < b.end && pb.end > b.start);
          if (overlaps) { skipped.push(dayLabels[d]); continue; }
          next[d] = { ...prev[d], breaks: [...existing, { start: pb.start, end: pb.end }].sort((a, b) => a.start.localeCompare(b.start)) };
        }
        return next;
      });
      if (skipped.length > 0) alert(`Pause konnte nicht für alle Tage übernommen werden: ${skipped.join(', ')}`);
    } else {
      const dayStart = businessHours[day]?.start || '00:00';
      const dayEnd = businessHours[day]?.end || '23:59';
      if (pb.start < dayStart || pb.end > dayEnd) { alert(`Pause muss innerhalb der Öffnungszeiten liegen (${dayStart}–${dayEnd}).`); return; }
      const existing = businessHours[day].breaks ?? [];
      const overlaps = existing.some(b => pb.start < b.end && pb.end > b.start);
      if (overlaps) { alert('Diese Pause überschneidet sich mit einer bestehenden Pause.'); return; }
      setBusinessHours(prev => ({ ...prev, [day]: { ...prev[day], breaks: [...existing, { start: pb.start, end: pb.end }].sort((a, b) => a.start.localeCompare(b.start)) } }));
    }
    setPendingBreak(prev => ({ ...prev, [day]: { ...prev[day], visible: false, applyToAll: false } }));
  };

  const handleCancelBreak = (day: DayKey) => {
    setPendingBreak(prev => ({ ...prev, [day]: { ...prev[day], visible: false } }));
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const anyEnabled = days.some(day => businessHours[day]?.enabled);
    if (!anyEnabled) { alert('Mindestens ein Tag muss geöffnet sein!'); return; }
    for (const day of days) {
      const hours = businessHours[day];
      if (hours?.enabled && hours.start >= hours.end) { alert(`${dayLabels[day]}: Startzeit muss vor Endzeit liegen!`); return; }
      if (hours?.enabled) {
        for (const brk of hours.breaks ?? []) {
          if (brk.start >= brk.end) { alert(`${dayLabels[day]}: Pausenanfang muss vor Pausenende liegen.`); return; }
          if (brk.start < hours.start || brk.end > hours.end) { alert(`${dayLabels[day]}: Pause muss innerhalb der Öffnungszeiten liegen.`); return; }
        }
      }
    }
    onSave({ ...settings, business_hours: businessHours });
  };

  const timeInputCls = "w-[5.5rem] text-center tabular-nums bg-white border border-[#E8EAF6] text-gray-900 text-sm px-3 py-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF6D00]/15 focus:border-[#FF6D00] disabled:opacity-40 disabled:cursor-not-allowed transition-all";

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="bg-white rounded-[18px] border border-[#E8EAF6] shadow-[0_2px_12px_rgba(14,11,30,0.04)] overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-[#E8EAF6]">
          <div className="text-sm font-bold text-[#0E0B1E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Öffnungszeiten</div>
          <div className="text-xs text-[#654F6F] mt-0.5">Lege fest, an welchen Tagen und zu welchen Zeiten Termine gebucht werden können.</div>
        </div>
        <div className="p-4 space-y-2">
        {days.map(day => {
          const dayHours = businessHours[day];
          const isEnabled = dayHours?.enabled || false;
          const breaks = dayHours?.breaks ?? [];
          const pb = pendingBreak[day];

          return (
            <div key={day} className={cn("rounded-2xl border transition-all", isEnabled ? "bg-white border-[#E8EAF6] shadow-sm" : "bg-[#F4F5FC] border-[#E8EAF6]")}>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-3 p-4">
                <button
                  type="button"
                  onClick={() => handleDayChange(day, 'enabled', !isEnabled)}
                  className={cn("w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all flex-shrink-0", isEnabled ? "bg-[#FF6D00] border-[#FF6D00] text-white" : "bg-white border-gray-300")}
                >
                  {isEnabled && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                </button>
                <label className={cn("text-sm font-bold w-24 cursor-pointer", isEnabled ? "text-[#0E0B1E]" : "text-gray-400")} onClick={() => handleDayChange(day, 'enabled', !isEnabled)}>
                  {dayLabels[day]}
                </label>
                <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-[12rem]">
                  <TimeInput value={dayHours?.start || '08:00'} onChange={v => handleDayChange(day, 'start', v)} disabled={!isEnabled} className={timeInputCls} />
                  <span className="text-gray-300 font-light">—</span>
                  <TimeInput value={dayHours?.end || '18:00'} onChange={v => handleDayChange(day, 'end', v)} disabled={!isEnabled} className={timeInputCls} />
                </div>
              </div>

              {isEnabled && (
                <div className="px-4 pb-4 border-t border-[#E8EAF6]">
                  <p className="text-[10px] font-bold text-[#654F6F] uppercase tracking-widest mt-3 mb-2">Pausen</p>
                  {breaks.length > 0 && (
                    <div className="space-y-1.5 mb-2">
                      {breaks.map((brk, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm text-gray-600 bg-[#F4F5FC] px-3 py-1.5 rounded-lg w-fit">
                          <span className="font-mono text-xs font-semibold">{brk.start}</span>
                          <span className="text-gray-300">–</span>
                          <span className="font-mono text-xs font-semibold">{brk.end}</span>
                          <button type="button" onClick={() => handleDeleteBreak(day, idx)} className="ml-1 -my-1 text-gray-400 hover:text-red-500 transition-colors w-7 h-7 sm:w-4 sm:h-4 sm:my-0 flex items-center justify-center rounded" aria-label="Pause löschen">
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {pb.visible ? (
                    <div className="mt-2 space-y-2 p-3 bg-[#F4F5FC] rounded-xl border border-[#E8EAF6]">
                      <div className="flex flex-wrap items-center gap-2">
                        <TimeInput value={pb.start} onChange={v => setPendingBreak(prev => ({ ...prev, [day]: { ...prev[day], start: v } }))} className={timeInputCls} />
                        <span className="text-gray-300">–</span>
                        <TimeInput value={pb.end} onChange={v => setPendingBreak(prev => ({ ...prev, [day]: { ...prev[day], end: v } }))} className={timeInputCls} />
                        <button type="button" onClick={() => handleConfirmBreak(day)} className="px-3 py-1.5 bg-[#FF6D00] text-white text-xs font-bold rounded-lg hover:bg-[#E85F00] transition-colors">OK</button>
                        <button type="button" onClick={() => handleCancelBreak(day)} className="px-3 py-1.5 border border-[#E8EAF6] text-gray-600 text-xs font-semibold rounded-lg hover:bg-gray-100 transition-colors">Abbrechen</button>
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input type="checkbox" checked={pb.applyToAll} onChange={e => setPendingBreak(prev => ({ ...prev, [day]: { ...prev[day], applyToAll: e.target.checked } }))} className="rounded border-gray-300 accent-[#FF6D00] cursor-pointer" />
                        <span className="text-xs text-gray-500 font-medium">Diese Pause als Standard für alle aktiven Tage festlegen</span>
                      </label>
                      {pb.applyToAll && (
                        <div className="text-xs text-gray-400 pl-6">
                          Angewendet auf: <span className="font-semibold text-[#0E0B1E]">{days.filter(d => businessHours[d].enabled).map(d => dayLabels[d]).join(', ')}</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <button type="button" onClick={() => handleShowAddBreak(day)} className="text-xs text-[#654F6F] hover:text-[#FF6D00] transition-colors flex items-center gap-1 mt-1 font-semibold">
                      <span className="text-base leading-none">+</span> Pause hinzufügen
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
        </div>
      </div>

      <div className="flex justify-end">
        <button type="submit" disabled={saving} className={saveBtnCls} style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          {saving ? <><Icons.Loader /><span>Speichert...</span></> : <><Icons.Check /><span>Speichern</span></>}
        </button>
      </div>
    </form>
  );
}

// --- APPOINTMENTS TAB ---
function AppointmentsTab({ settings, onSave, saving }: { settings: Settings | null; onSave: (s: Settings) => void; saving: boolean }) {
  const [mode, setMode] = useState<'single' | 'multiple'>(settings?.appointment_durations?.mode || 'single');
  const [defaultDuration, setDefaultDuration] = useState(settings?.appointment_durations?.default || settings?.default_duration || 60);
  const [types, setTypes] = useState(settings?.appointment_durations?.types || []);
  const [defaultGap, setDefaultGap] = useState(settings?.default_gap || 15);
  const [preventUnfillableGaps, setPreventUnfillableGaps] = useState(settings?.gap_rules?.prevent_unfillable_gaps || false);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [editingType, setEditingType] = useState<any>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (defaultDuration <= 0) { alert('Termindauer muss größer als 0 sein!'); return; }
    if (defaultDuration % 5 !== 0) { alert('Termindauer muss ein Vielfaches von 5 sein!'); return; }
    if (mode === 'multiple' && types.length === 0) { alert('Mindestens ein Termintyp muss definiert sein!'); return; }
    if (defaultGap < 0) { alert('Pause muss 0 oder größer sein!'); return; }
    if (mode === 'multiple' && types.length > 0) {
      const ids = types.map((t) => String(t.id || '').trim());
      if (ids.some((id) => !id)) {
        alert('Jeder Termintyp braucht eine eindeutige interne ID. Speichere die Termintypen erneut oder bearbeite Einträge ohne ID.');
        return;
      }
      const seen = new Set();
      for (const id of ids) {
        if (seen.has(id)) {
          alert(`Doppelte interne ID "${id}" bei mehreren Termintypen. Bitte in den Einstellungen einen Namen eindeutiger machen oder IDs bereinigen.`);
          return;
        }
        seen.add(id);
      }
    }
    onSave({
      ...settings,
      appointment_durations: { mode, default: defaultDuration, types: mode === 'multiple' ? types : [] },
      default_duration: defaultDuration,
      default_gap: defaultGap,
      gap_rules: { mode: settings?.gap_rules?.mode || 'flexible', min_gap_minutes: settings?.gap_rules?.min_gap_minutes ?? defaultGap, gap_step: settings?.gap_rules?.gap_step ?? 5, allow_zero_gap: settings?.gap_rules?.allow_zero_gap ?? true, prevent_unfillable_gaps: preventUnfillableGaps }
    });
  };

  const handleAddType = () => { setEditingType({ id: '', name: '', duration: 60, color: '#3B82F6' }); setEditingIndex(null); setShowTypeModal(true); };
  const handleEditType = (type: any, index: number) => { setEditingType({ ...type }); setEditingIndex(index); setShowTypeModal(true); };
  const handleDeleteType = (index: number) => { if (confirm('Termintyp wirklich löschen?')) setTypes(types.filter((_, i) => i !== index)); };

  const handleSaveType = () => {
    const nameTrim = editingType.name?.trim() || '';
    if (!nameTrim) { alert('Name ist erforderlich!'); return; }
    if (editingType.duration <= 0 || editingType.duration % 5 !== 0) { alert('Dauer muss positiv und ein Vielfaches von 5 sein!'); return; }
    const isDuplicateName = types.some((t, i) => (t.name || '').trim() === nameTrim && i !== editingIndex);
    if (isDuplicateName) { alert('Ein Termintyp mit diesem Namen existiert bereits!'); return; }
    const slugFromName = nameTrim.toLowerCase().replace(/\s+/g, '_');
    const resolvedId = String(editingType.id || '').trim() || slugFromName;
    if (!resolvedId) { alert('Interne ID konnte nicht gesetzt werden.'); return; }
    const isDuplicateId = types.some((t, i) => String(t.id || '').trim() === resolvedId && i !== editingIndex);
    if (isDuplicateId) {
      alert('Zwei Termintypen haben dieselbe interne ID (oft durch sehr ähnliche Namen mit extra Leerzeichen). Bitte den Namen eindeutiger gestalten.');
      return;
    }
    const saved = { ...editingType, name: nameTrim, id: resolvedId };
    if (editingIndex !== null) {
      const newTypes = [...types]; newTypes[editingIndex] = saved; setTypes(newTypes);
    } else {
      if (types.length >= 10) { alert('Maximal 10 Termintypen erlaubt!'); return; }
      setTypes([...types, saved]);
    }
    setShowTypeModal(false); setEditingType(null); setEditingIndex(null);
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="bg-white rounded-[18px] border border-[#E8EAF6] shadow-[0_2px_12px_rgba(14,11,30,0.04)] overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-[#E8EAF6]">
          <div className="text-sm font-bold text-[#0E0B1E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Termin-Einstellungen</div>
          <div className="text-xs text-[#654F6F] mt-0.5">Konfiguriere die Termindauer und Pausen zwischen Terminen.</div>
        </div>
        <div className="p-4 sm:p-6">
      <div className="space-y-6">
        {/* Mode Toggle */}
        <div>
          <label className={labelCls}>Termindauer-Modus</label>
          <div className="flex gap-3">
            {[{ v: 'single', title: 'Einheitliche Dauer', desc: 'Alle Termine haben die gleiche Länge' }, { v: 'multiple', title: 'Verschiedene Längen', desc: 'Mehrere Termintypen mit unterschiedlichen Dauern' }].map(opt => (
              <button key={opt.v} type="button" onClick={() => setMode(opt.v as any)}
                className={cn("flex-1 px-4 py-3.5 rounded-xl border-2 transition-all text-left",
                  mode === opt.v ? "border-[#FF6D00] bg-[#FF6D00]/5 shadow-[0_0_0_1px_rgba(255,109,0,0.1)]" : "border-[#E8EAF6] hover:border-[#654F6F]/40 bg-white"
                )}>
                <div className={cn("text-sm font-bold", mode === opt.v ? "text-[#FF6D00]" : "text-[#0E0B1E]")}>{opt.title}</div>
                <div className="text-xs text-gray-400 mt-1">{opt.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Default Duration */}
        <div>
          <label className={labelCls}>{mode === 'single' ? 'Termindauer (Minuten)' : 'Standard-Termindauer (Minuten)'}</label>
          <input type="number" min="5" step="5" value={defaultDuration} onChange={e => setDefaultDuration(parseInt(e.target.value) || 0)} className={inputCls} />
          <p className="text-xs text-gray-400 mt-1.5">{mode === 'single' ? 'Dauer für alle Termine (z.B. 30, 60, 90 Minuten).' : 'Standard-Dauer als Fallback (z.B. 60 Minuten).'}</p>
        </div>

        {/* Appointment Types */}
        {mode === 'multiple' && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className={labelCls}>Termintypen ({types.length}/10)</label>
              <button type="button" onClick={handleAddType} className="flex items-center gap-1.5 text-xs font-bold text-[#FF6D00] hover:text-[#E85F00] transition-colors">
                <Icons.Plus /> Typ hinzufügen
              </button>
            </div>
            {types.length === 0 ? (
              <div className="p-4 sm:p-6 bg-[#F4F5FC] border border-[#E8EAF6] rounded-xl text-center">
                <p className="text-sm text-gray-400">Noch keine Termintypen definiert.</p>
                <button type="button" onClick={handleAddType} className="mt-2 text-sm text-[#FF6D00] font-semibold hover:underline">Ersten Typ erstellen</button>
              </div>
            ) : (
              <div className="space-y-2">
                {types.map((type, index) => (
                  <div key={index} className="flex items-center gap-3 p-3.5 bg-white border border-[#E8EAF6] rounded-xl hover:border-[#FF6D00]/30 transition-all">
                    <div className="w-4 h-4 rounded-full flex-shrink-0 shadow-sm" style={{ backgroundColor: type.color }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-[#0E0B1E]">{type.name}</div>
                      <div className="text-xs text-gray-400">{type.duration} Minuten</div>
                    </div>
                    <button type="button" onClick={() => handleEditType(type, index)} className="text-xs text-[#654F6F] hover:text-[#0E0B1E] font-semibold px-2 py-1 rounded-lg hover:bg-[#F4F5FC] transition-colors">Bearbeiten</button>
                    <button type="button" onClick={() => handleDeleteType(index)} className="text-xs text-red-500 hover:text-red-700 font-semibold px-2 py-1 rounded-lg hover:bg-red-50 transition-colors">Löschen</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Default Gap */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <label className={labelCls + " mb-0"}>Standard-Pause (Minuten)</label>
            <div className="relative group">
              <div className="text-gray-400 hover:text-[#654F6F] cursor-help"><Icons.Info /></div>
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-50">
                <div className="bg-[#0E0B1E] text-white text-xs rounded-xl px-4 py-3 w-64 shadow-xl">
                  <p className="font-bold mb-1">Standard-Pause für alle Ressourcen</p>
                  <p className="text-white/70">Diese Pause wird verwendet, wenn eine Ressource keine eigene Pause definiert hat.</p>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1"><div className="border-4 border-transparent border-t-[#0E0B1E]"></div></div>
                </div>
              </div>
            </div>
          </div>
          <input type="number" min="0" step="5" value={defaultGap} onChange={e => setDefaultGap(parseInt(e.target.value) || 0)} className={inputCls} />
          <p className="text-xs text-gray-400 mt-1.5">Pause zwischen Terminen (z.B. 0, 10, 15, 20 Minuten).</p>
        </div>

        {/* Gap Prevention */}
        <div className="pt-5 border-t border-[#E8EAF6]">
          <div className="flex items-start gap-3 p-4 bg-[#F4F5FC] rounded-xl border border-[#E8EAF6]">
            <button type="button" onClick={() => setPreventUnfillableGaps(!preventUnfillableGaps)}
              className={cn("flex-shrink-0 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all mt-0.5", preventUnfillableGaps ? "bg-[#FF6D00] border-[#FF6D00] text-white" : "bg-white border-gray-300")}>
              {preventUnfillableGaps && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>}
            </button>
            <div className="flex-1">
              <label className="text-sm font-bold text-[#0E0B1E] cursor-pointer" onClick={() => setPreventUnfillableGaps(!preventUnfillableGaps)}>Unfüllbare Lücken verhindern</label>
              <p className="text-xs text-gray-500 mt-1 leading-relaxed">Wenn aktiviert, können Termine nur gebucht werden, wenn sie keine Lücken hinterlassen, die zu klein für weitere Termine sind.</p>
              <div className="mt-3 p-3 bg-white border border-[#E8EAF6] rounded-xl">
                <p className="text-xs text-gray-600">
                  <strong>Berechnung:</strong> Minimale füllbare Lücke = kürzeste Termindauer + Pause
                  {mode === 'multiple' && types.length > 0 ? (
                    <><br /><strong>Beispiel:</strong> {Math.min(...types.map(t => t.duration))} Min + {defaultGap} Min = <strong className="text-[#FF6D00]">{Math.min(...types.map(t => t.duration)) + defaultGap} Min</strong></>
                  ) : (
                    <><br /><strong>Beispiel:</strong> {defaultDuration} Min + {defaultGap} Min = <strong className="text-[#FF6D00]">{defaultDuration + defaultGap} Min</strong></>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 bg-[#00B0FF]/5 border border-[#00B0FF]/20 rounded-xl">
          <p className="text-xs text-[#0076E8] font-medium"><strong>Hinweis:</strong> Erweiterte Pausen-Regeln (Strict/Flexible/None) werden in Phase 2 implementiert.</p>
        </div>
        </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button type="submit" disabled={saving} className={saveBtnCls} style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          {saving ? <><Icons.Loader /><span>Speichert...</span></> : <><Icons.Check /><span>Speichern</span></>}
        </button>
      </div>

      {/* Type Edit Modal */}
      {showTypeModal && (
        <div className="fixed inset-0 bg-[#0E0B1E]/40 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <motion.div initial={{ opacity: 0, scale: 0.95, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }} className="bg-white rounded-2xl p-4 sm:p-6 max-w-md w-full shadow-[0_24px_64px_rgba(14,11,30,0.2)] border border-[#E8EAF6] max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain">
            <h3 className="text-base font-bold text-[#0E0B1E] mb-5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {editingIndex !== null ? 'Termintyp bearbeiten' : 'Neuer Termintyp'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className={labelCls}>Name *</label>
                <input type="text" value={editingType?.name || ''} onChange={e => setEditingType({ ...editingType, name: e.target.value })} placeholder="z.B. Beratung" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Dauer (Minuten) *</label>
                <input type="number" min="5" step="5" value={editingType?.duration || 60} onChange={e => setEditingType({ ...editingType, duration: parseInt(e.target.value) || 0 })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Farbe</label>
                <div className="flex items-center gap-3">
                  <input type="color" value={editingType?.color || '#3B82F6'} onChange={e => setEditingType({ ...editingType, color: e.target.value })} className="w-14 h-11 rounded-xl border border-[#E8EAF6] cursor-pointer" />
                  <span className="text-sm text-gray-500 font-mono">{editingType?.color || '#3B82F6'}</span>
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button type="button" onClick={() => { setShowTypeModal(false); setEditingType(null); setEditingIndex(null); }} className="flex-1 px-4 py-2.5 bg-[#F4F5FC] text-gray-700 text-sm font-semibold rounded-xl hover:bg-gray-200 transition-colors">Abbrechen</button>
              <button type="button" onClick={handleSaveType} className="flex-1 px-4 py-2.5 bg-gradient-to-r from-[#FF6D00] to-[#FF9A00] text-white text-sm font-bold rounded-xl shadow-[0_4px_12px_rgba(255,109,0,0.25)] transition-all" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Speichern</button>
            </div>
          </motion.div>
        </div>
      )}
    </form>
  );
}

// --- RESOURCE FORM FIELDS ---
// Must be defined at module level (not inside ResourcesTab) so React never treats it as a new
// component type on re-render — which would remount all inputs and lose focus on every keystroke.
type ResourceFormData = { name: string; email: string; color: string; enabled: boolean; capacity: string };
function ResourceFormFields({
  formData,
  onFormDataChange,
  onSubmit,
  onCancel,
}: {
  formData: ResourceFormData;
  onFormDataChange: (data: ResourceFormData) => void;
  onSubmit: (e: FormEvent) => void;
  onCancel: () => void;
}) {
  return (
    <form onSubmit={onSubmit} className="p-4 sm:p-6 space-y-4">
      {([
        { label: 'Name *', type: 'text', key: 'name' as const, placeholder: 'z.B. Anna Müller', required: true },
        { label: 'E-Mail (optional)', type: 'email', key: 'email' as const, placeholder: 'email@beispiel.de', required: false },
      ] as const).map(field => (
        <div key={field.key}>
          <label className={labelCls}>{field.label}</label>
          <input
            type={field.type}
            required={field.required}
            value={formData[field.key]}
            onChange={e => onFormDataChange({ ...formData, [field.key]: e.target.value })}
            placeholder={field.placeholder}
            className={inputCls}
          />
        </div>
      ))}
      <div>
        <label className={labelCls}>Personen (optional)</label>
        <input
          type="number"
          min={1}
          max={20}
          step={1}
          value={formData.capacity}
          onChange={e => onFormDataChange({ ...formData, capacity: e.target.value })}
          placeholder="z.B. 4"
          className={inputCls}
        />
        <p className="text-xs text-gray-400 mt-1.5">Wie viele Personen passen an diese Ressource, z.B. an einen Tisch.</p>
      </div>
      <div>
        <label className={labelCls}>Farbe</label>
        <div className="flex items-center gap-3">
          <input type="color" value={formData.color} onChange={e => onFormDataChange({ ...formData, color: e.target.value })} className="w-14 h-11 rounded-xl border border-[#E8EAF6] cursor-pointer" />
          <span className="text-sm text-gray-500 font-mono">{formData.color}</span>
        </div>
      </div>
      <div className="flex items-center gap-3 p-3.5 bg-[#F4F5FC] rounded-xl">
        <input type="checkbox" id="enabled-form" checked={formData.enabled} onChange={e => onFormDataChange({ ...formData, enabled: e.target.checked })} className="w-4 h-4 rounded border-gray-300 accent-[#FF6D00] cursor-pointer" />
        <label htmlFor="enabled-form" className="text-sm font-semibold text-[#0E0B1E] cursor-pointer">Ressource ist aktiv</label>
      </div>
      <div className="flex gap-3 pt-2">
        <button type="button" onClick={onCancel} className="flex-1 px-4 py-2.5 bg-[#F4F5FC] text-gray-700 text-sm font-semibold rounded-xl hover:bg-gray-200 transition-colors">Abbrechen</button>
        <button type="submit" className="flex-1 px-4 py-2.5 bg-gradient-to-r from-[#FF6D00] to-[#FF9A00] text-white text-sm font-bold rounded-xl shadow-[0_4px_12px_rgba(255,109,0,0.25)] transition-all" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Speichern</button>
      </div>
    </form>
  );
}

// --- RESOURCES TAB ---
function ResourcesTab({
  settings,
  saving,
  onResourceChange,
  getToken,
  onSaveResourceLabels,
  onSettingsSynced,
}: {
  settings: Settings | null;
  saving: boolean;
  onResourceChange?: () => void;
  getToken: () => Promise<string>;
  onSaveResourceLabels: (singular: string, plural: string) => Promise<void>;
  onSettingsSynced?: () => Promise<void>;
}) {
  const [resources, setResources] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedResource, setSelectedResource] = useState<any | null>(null);
  const [formData, setFormData] = useState<ResourceFormData>({ name: '', email: '', color: '#3B82F6', enabled: true, capacity: '' });
  const [labelSingular, setLabelSingular] = useState(settings?.resource_label?.singular || '');
  const [labelPlural, setLabelPlural] = useState(settings?.resource_label?.plural || '');

  useEffect(() => {
    setLabelSingular(settings?.resource_label?.singular || '');
    setLabelPlural(settings?.resource_label?.plural || '');
  }, [settings?.resource_label?.singular, settings?.resource_label?.plural]);

  const fetchResources = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/admin/resources`, { headers: { Authorization: `Bearer ${await getToken()}` } });
      setResources(res.data.resources || []);
    } catch (err: any) { console.error('[RESOURCES-UI] Failed to load resources:', err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchResources(); }, []);

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const payload = { ...formData, capacity: formData.capacity === '' ? undefined : Number(formData.capacity) };
      await axios.post(`${API_BASE_URL}/admin/resources`, payload, { headers: { Authorization: `Bearer ${await getToken()}` } });
      setShowAddModal(false);
      setFormData({ name: '', email: '', color: '#3B82F6', enabled: true, capacity: '' });
      await fetchResources();
      await onSettingsSynced?.();
      onResourceChange?.();
    } catch (err: any) { alert(err.response?.data?.error || 'Fehler beim Erstellen der Ressource'); }
  };

  const handleUpdate = async (e: FormEvent) => {
    e.preventDefault();
    if (!selectedResource) return;
    try {
      const payload = { ...formData, capacity: formData.capacity === '' ? null : Number(formData.capacity) };
      await axios.put(`${API_BASE_URL}/admin/resources/${selectedResource.id}`, payload, { headers: { Authorization: `Bearer ${await getToken()}` } });
      setShowEditModal(false); setSelectedResource(null);
      setFormData({ name: '', email: '', color: '#3B82F6', enabled: true, capacity: '' });
      await fetchResources();
      await onSettingsSynced?.();
      onResourceChange?.();
    } catch (err: any) { alert(err.response?.data?.error || 'Fehler beim Aktualisieren der Ressource'); }
  };

  const handleDelete = async () => {
    if (!selectedResource) return;
    try {
      await axios.delete(`${API_BASE_URL}/admin/resources/${selectedResource.id}`, { headers: { Authorization: `Bearer ${await getToken()}` } });
      setShowDeleteModal(false); setSelectedResource(null);
      await fetchResources();
      await onSettingsSynced?.();
      onResourceChange?.();
    } catch (err: any) { alert(err.response?.data?.error || 'Fehler beim Löschen der Ressource'); }
  };

  const openEditModal = (resource: any) => {
    setSelectedResource(resource);
    setFormData({
      name: resource.name || resource.id || resource,
      email: resource.email || '',
      color: resource.color || '#3B82F6',
      enabled: resource.enabled !== undefined ? resource.enabled : true,
      capacity: resource.capacity !== undefined && resource.capacity !== null ? String(resource.capacity) : '',
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (resource: any) => { setSelectedResource(resource); setShowDeleteModal(true); };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="w-8 h-8 border-2 border-[#E8EAF6] border-t-[#FF6D00] rounded-full animate-spin" />
      </div>
    );
  }


  const handleLabelSave = async (e: FormEvent) => {
    e.preventDefault();
    await onSaveResourceLabels(labelSingular.trim(), labelPlural.trim());
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Resource Label Section */}
      <form onSubmit={handleLabelSave} className="flex flex-col gap-4">
        <div className="bg-white rounded-[18px] border border-[#E8EAF6] shadow-[0_2px_12px_rgba(14,11,30,0.04)] overflow-hidden">
          <div className="px-4 sm:px-6 py-4 border-b border-[#E8EAF6]">
            <div className="text-sm font-bold text-[#0E0B1E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Bezeichnung</div>
            <div className="text-xs text-[#654F6F] mt-0.5">Wie heißen deine Ressourcen? Der Chatbot verwendet diese Begriffe in allen Antworten.</div>
          </div>
          <div className="p-4 sm:p-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Einzahl</label>
                <input
                  type="text"
                  value={labelSingular}
                  onChange={e => setLabelSingular(e.target.value)}
                  placeholder="z.B. Friseur, Therapeut, Coach"
                  className={inputCls}
                />
                <p className="text-xs text-gray-400 mt-1.5">Wird für einzelne Ressource verwendet, z.B. "bei welchem Friseur".</p>
              </div>
              <div>
                <label className={labelCls}>Mehrzahl</label>
                <input
                  type="text"
                  value={labelPlural}
                  onChange={e => setLabelPlural(e.target.value)}
                  placeholder="z.B. Friseure, Therapeuten, Coaches"
                  className={inputCls}
                />
                <p className="text-xs text-gray-400 mt-1.5">Wird für mehrere Ressourcen verwendet, z.B. "unsere Friseure".</p>
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end">
          <button type="submit" disabled={saving} className={saveBtnCls} style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {saving ? <><Icons.Loader /><span>Speichert...</span></> : <><Icons.Check /><span>Speichern</span></>}
          </button>
        </div>
      </form>

      {/* Resources List Section */}
      <div className="bg-white rounded-[18px] border border-[#E8EAF6] shadow-[0_2px_12px_rgba(14,11,30,0.04)] overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-[#E8EAF6] flex items-center justify-between">
          <div>
            <div className="text-sm font-bold text-[#0E0B1E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Ressourcen-Verwaltung</div>
            <div className="text-xs text-[#654F6F] mt-0.5">Verwalte deine Ressourcen und Mitarbeiter.</div>
          </div>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => { setFormData({ name: '', email: '', color: '#3B82F6', enabled: true, capacity: '' }); setShowAddModal(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#FF6D00] to-[#FF9A00] text-white text-xs font-bold rounded-xl shadow-[0_4px_12px_rgba(255,109,0,0.25)] transition-all"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            <Icons.Plus /><span>Neue Ressource</span>
          </motion.button>
        </div>
        <div className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {resources.map((resource, index) => {
          const resourceId = typeof resource === 'string' ? resource : resource.id;
          const resourceName = typeof resource === 'string' ? resource : resource.name;
          const resourceEmail = typeof resource === 'object' ? resource.email : null;
          const resourceColor = typeof resource === 'object' ? resource.color : '#3B82F6';
          const resourceEnabled = typeof resource === 'object' ? resource.enabled : true;
          const resourceCapacity = typeof resource === 'object' ? resource.capacity : undefined;

          return (
            <motion.div
              key={resourceId || index}
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white border border-[#E8EAF6] rounded-2xl overflow-hidden hover:shadow-[0_4px_20px_rgba(14,11,30,0.08)] transition-all"
            >
              <div className="h-1 w-full" style={{ backgroundColor: resourceColor }} />
              <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow-sm" style={{ backgroundColor: resourceColor }}>
                      {resourceName?.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-bold text-[#0E0B1E] truncate">{resourceName}</h4>
                      {resourceEmail && <p className="text-xs text-gray-400 truncate">{resourceEmail}</p>}
                      {typeof resourceCapacity === 'number' && (
                        <p className="text-xs text-[#654F6F] font-semibold truncate">{resourceCapacity} Personen</p>
                      )}
                    </div>
                  </div>
                  <div className={cn("px-2 py-0.5 rounded-lg text-[10px] font-bold uppercase tracking-wide ml-2 flex-shrink-0",
                    resourceEnabled ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-gray-100 text-gray-400 border border-gray-200"
                  )}>
                    {resourceEnabled ? 'Aktiv' : 'Inaktiv'}
                  </div>
                </div>

                <div className="flex gap-2 pt-2 border-t border-[#E8EAF6]">
                  <button onClick={() => openEditModal(resource)} className="flex-1 px-3 py-2 text-xs font-semibold text-[#654F6F] bg-[#F4F5FC] hover:bg-[#E8EAF6] rounded-xl transition-colors">Bearbeiten</button>
                  <button onClick={() => openDeleteModal(resource)} className="flex-1 px-3 py-2 text-xs font-semibold text-red-500 bg-red-50 hover:bg-red-100 rounded-xl transition-colors">Löschen</button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

        {resources.length === 0 && (
          <div className="text-center py-12 bg-[#F4F5FC] rounded-2xl border border-[#E8EAF6]">
            <div className="w-14 h-14 bg-white rounded-2xl border border-[#E8EAF6] flex items-center justify-center mx-auto mb-4 text-[#654F6F]">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            </div>
            <p className="text-sm text-gray-400 font-medium mb-4">Noch keine Ressourcen vorhanden.</p>
            <button onClick={() => { setFormData({ name: '', email: '', color: '#3B82F6', enabled: true, capacity: '' }); setShowAddModal(true); }}
              className="px-5 py-2.5 bg-gradient-to-r from-[#FF6D00] to-[#FF9A00] text-white text-sm font-bold rounded-xl shadow-[0_4px_12px_rgba(255,109,0,0.25)] transition-all"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Erste Ressource erstellen
            </button>
          </div>
        )}
        </div>
      </div>

      {/* Add Modal */}
      <AnimatePresence>
        {showAddModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-[#0E0B1E]/40 backdrop-blur-md" onClick={() => setShowAddModal(false)} />
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 12 }} transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="bg-white w-full max-w-md rounded-2xl shadow-[0_24px_64px_rgba(14,11,30,0.2)] relative z-10 border border-[#E8EAF6] max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain">
              <div className="px-4 sm:px-6 py-5 bg-gradient-to-r from-[#FF6D00]/6 to-transparent border-b border-[#E8EAF6] flex justify-between items-center">
                <h3 className="text-base font-bold text-[#0E0B1E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Neue Ressource erstellen</h3>
                <button onClick={() => setShowAddModal(false)} aria-label="Schließen" className="w-9 h-9 sm:w-7 sm:h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400"><Icons.X /></button>
              </div>
              <ResourceFormFields formData={formData} onFormDataChange={setFormData} onSubmit={handleCreate} onCancel={() => setShowAddModal(false)} />
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Edit Modal */}
      <AnimatePresence>
        {showEditModal && selectedResource && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-[#0E0B1E]/40 backdrop-blur-md" onClick={() => setShowEditModal(false)} />
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 12 }} transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="bg-white w-full max-w-md rounded-2xl shadow-[0_24px_64px_rgba(14,11,30,0.2)] relative z-10 border border-[#E8EAF6] max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain">
              <div className="px-4 sm:px-6 py-5 bg-gradient-to-r from-[#654F6F]/5 to-transparent border-b border-[#E8EAF6] flex justify-between items-center">
                <h3 className="text-base font-bold text-[#0E0B1E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Ressource bearbeiten</h3>
                <button onClick={() => setShowEditModal(false)} aria-label="Schließen" className="w-9 h-9 sm:w-7 sm:h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400"><Icons.X /></button>
              </div>
              <ResourceFormFields formData={formData} onFormDataChange={setFormData} onSubmit={handleUpdate} onCancel={() => setShowEditModal(false)} />
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Delete Modal */}
      <AnimatePresence>
        {showDeleteModal && selectedResource && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-[#0E0B1E]/40 backdrop-blur-md" onClick={() => setShowDeleteModal(false)} />
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 12 }} transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="bg-white w-full max-w-md rounded-2xl shadow-[0_24px_64px_rgba(14,11,30,0.2)] relative z-10 border border-[#E8EAF6] max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain">
              <div className="px-4 sm:px-6 py-5 border-b border-[#E8EAF6]">
                <h3 className="text-base font-bold text-[#0E0B1E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Ressource löschen?</h3>
              </div>
              <div className="p-4 sm:p-6">
                <p className="text-sm text-gray-600 mb-4">
                  Möchtest du die Ressource <strong className="text-[#0E0B1E]">{selectedResource.name || selectedResource.id || selectedResource}</strong> wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-5">
                  <p className="text-xs text-amber-800 font-medium"><strong>Hinweis:</strong> Ressourcen mit aktiven Terminen können nicht gelöscht werden.</p>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => setShowDeleteModal(false)} className="flex-1 px-4 py-2.5 bg-[#F4F5FC] text-gray-700 text-sm font-semibold rounded-xl hover:bg-gray-200 transition-colors">Abbrechen</button>
                  <button onClick={handleDelete} className="flex-1 px-4 py-2.5 bg-red-600 text-white text-sm font-bold rounded-xl hover:bg-red-700 transition-colors">Löschen</button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

// --- DATENSCHUTZ TAB (DSGVO / GDPR) ---
function DatenschutzTab({ getToken }: { getToken: () => Promise<string> }) {
  const [deleting, setDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportSuccess, setExportSuccess] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    setExportSuccess(false);
    try {
      const token = await getToken();
      const res = await axios.get(`${API_BASE_URL}/admin/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const disposition = String(res.headers['content-disposition'] || '');
      const match = disposition.match(/filename="?([^"]+)"?/i);
      const filename = match?.[1] || `candlear-export-${new Date().toISOString().slice(0, 10)}.json`;
      const blob = new Blob([res.data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setExportSuccess(true);
      setTimeout(() => setExportSuccess(false), 4000);
    } catch (err: any) {
      let message = 'Fehler beim Datenexport.';
      const data = err.response?.data;
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          if (parsed?.error) message = parsed.error;
        } catch { /* keep default */ }
      } else if (typeof data?.error === 'string') {
        message = data.error;
      }
      setExportError(message);
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (confirmText !== 'LÖSCHEN') {
      alert('Bitte gib LÖSCHEN ein um zu bestätigen.');
      return;
    }
    if (!window.confirm('Bist du absolut sicher? Diese Aktion ist UNUMKEHRBAR. Alle deine Termine, Kunden und Einstellungen werden dauerhaft gelöscht.')) return;

    setDeleting(true);
    try {
      const token = await getToken();
      await axios.delete(`${API_BASE_URL}/admin/account`, { headers: { Authorization: `Bearer ${token}` } });
      alert('Dein Account und alle Daten wurden gelöscht.');
      await signOut(auth);
      window.location.href = '/';
    } catch (err: any) {
      alert(err.response?.data?.message || err.response?.data?.error || 'Fehler beim Löschen des Accounts.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="bg-white rounded-[18px] border border-[#E8EAF6] shadow-[0_2px_12px_rgba(14,11,30,0.04)] overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-[#E8EAF6]">
          <div className="text-sm font-bold text-[#0E0B1E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Datenschutz & DSGVO</div>
          <div className="text-xs text-[#654F6F] mt-0.5">Auskunft und Portabilität (Art. 15/20) sowie Löschung (Art. 17).</div>
        </div>
        <div className="p-4 sm:p-6 space-y-5">
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl space-y-2">
            <p className="text-sm font-bold text-blue-800">Deine gespeicherten Daten</p>
            <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside">
              <li>Kontodaten (E-Mail-Adresse über Firebase Authentication)</li>
              <li>Geschäftsdaten (Name, Öffnungszeiten, Ressourcen)</li>
              <li>Termine und Kundendaten</li>
              <li>Chat-Sitzungen des Buchungs-Widgets</li>
            </ul>
            <p className="text-xs text-blue-600 mt-2">Alle Daten sind nur für dich zugänglich. Deine Kerndaten (Account, Termine, Kunden) werden in der EU (Frankfurt, europe-west3) gespeichert. Einzelne Dienste (Hosting, KI-Assistent, E-Mail-Versand, Zahlungsabwicklung) laufen über Anbieter außerhalb der EU – Details in der Datenschutzerklärung.</p>
          </div>

          <div className="p-5 border border-[#E8EAF6] rounded-xl bg-[#F4F5FC] space-y-4">
            <div>
              <p className="text-sm font-bold text-[#0E0B1E] mb-1">Daten exportieren</p>
              <p className="text-xs text-[#654F6F] leading-relaxed">
                Lädt alle Mandantendaten als JSON-Datei: Account, Termine, Kunden, Chat-Verläufe und offene Buchungscodes.
                Stripe-IDs und API-Schlüssel sind nicht enthalten. Geeignet für Auskunftsanfragen oder Weitergabe an betroffene Personen.
              </p>
            </div>
            {exportError && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs text-red-700 font-semibold">{exportError}</div>
            )}
            {exportSuccess && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-xs text-emerald-700 font-semibold">
                Export heruntergeladen.
              </div>
            )}
            <button
              type="button"
              onClick={handleExport}
              disabled={exporting}
              className={saveBtnCls}
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              {exporting ? <><Icons.Loader /><span>Export wird erstellt...</span></> : <span>JSON-Export herunterladen</span>}
            </button>
          </div>

          <div className="p-5 border border-red-200 rounded-xl bg-red-50 space-y-4">
            <div>
              <p className="text-sm font-bold text-red-800 mb-1">Account dauerhaft löschen</p>
              <p className="text-xs text-red-600">Diese Aktion löscht deinen Account, alle Termine, Kunden, Einstellungen und Chat-Daten unwiderruflich. Dies kann nicht rückgängig gemacht werden.</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-red-700 mb-2">Gib <span className="font-bold">LÖSCHEN</span> ein um zu bestätigen</label>
              <input
                type="text"
                value={confirmText}
                onChange={e => setConfirmText(e.target.value)}
                placeholder="LÖSCHEN"
                className="w-full bg-white border border-red-300 text-red-900 text-sm px-3.5 py-2.5 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all"
              />
            </div>
            <button
              onClick={handleDeleteAccount}
              disabled={deleting || confirmText !== 'LÖSCHEN'}
              className="px-5 py-2.5 bg-red-600 text-white text-sm font-bold rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {deleting ? <><Icons.Loader /> Wird gelöscht...</> : 'Account und alle Daten löschen'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- EMAIL TAB ---
function EmailTab({ profile, onSaveProfile, saving }: { profile: AdminProfile; onSaveProfile: (p: AdminProfile) => void; saving: boolean }) {
  const [localName, setLocalName] = useState(profile.business_name);
  const [localEmail, setLocalEmail] = useState(profile.contact_email);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSaveProfile({ business_name: localName.trim(), contact_email: localEmail.trim() });
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="bg-white rounded-[18px] border border-[#E8EAF6] shadow-[0_2px_12px_rgba(14,11,30,0.04)] overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-[#E8EAF6]">
          <div className="text-sm font-bold text-[#0E0B1E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>E-Mail-Einstellungen</div>
          <div className="text-xs text-[#654F6F] mt-0.5">Firmenname und Benachrichtigungsadresse für automatische E-Mails.</div>
        </div>
        <div className="p-4 sm:p-6 space-y-5">
          <div>
            <label className={labelCls}>Firmenname</label>
            <input
              type="text"
              className={inputCls}
              value={localName}
              onChange={e => setLocalName(e.target.value)}
              placeholder="z.B. Friseursalon Muster"
            />
            <p className="text-xs text-gray-400 mt-1.5">Erscheint im Betreff und Text von Buchungsbestätigung und Code-Mail, die Kund:innen erhalten.</p>
          </div>

          <div>
            <label className={labelCls}>Benachrichtigungs-E-Mail</label>
            <input
              type="email"
              className={inputCls}
              value={localEmail}
              onChange={e => setLocalEmail(e.target.value)}
              placeholder="deine@email.de"
            />
            <p className="text-xs text-gray-400 mt-1.5">An diese Adresse erhältst du eine Benachrichtigung, wenn ein Kunde einen Termin storniert.</p>
          </div>

          <div className="p-4 bg-[#00B0FF]/5 border border-[#00B0FF]/20 rounded-xl flex items-start gap-3">
            <div className="w-7 h-7 bg-[#00B0FF]/15 rounded-lg flex items-center justify-center text-[#0076E8] flex-shrink-0 mt-0.5">
              <Icons.Info />
            </div>
            <p className="text-xs text-[#0076E8]/80 leading-relaxed">Erweiterte E-Mail-Funktionen (Templates, Erinnerungen) folgen in Phase 2.</p>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button type="submit" disabled={saving} className={saveBtnCls} style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          {saving ? <><Icons.Loader /><span>Speichert...</span></> : <><Icons.Check /><span>Speichern</span></>}
        </button>
      </div>
    </form>
  );
}
